import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';

// project imports
import { partnerLogin, partnerLogout, partnerMe } from 'api/portal';

// ==============================|| PARTNER AUTH CONTEXT ||============================== //
//
// Wraps the ops-runtime partner-auth flow (httpOnly `partnerToken` cookie).
// `me` on mount establishes the session; login/logout update it in place.

export const PartnerAuthContext = createContext(undefined);

export function PartnerAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isInitializing, setInitializing] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await partnerMe();
      setUser(res?.user || null);
      return res?.user || null;
    } catch {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      await refresh();
      if (active) setInitializing(false);
    })();
    return () => {
      active = false;
    };
  }, [refresh]);

  const login = useCallback(
    async (email, password, platformEndpoint) => {
      const res = await partnerLogin(email, password, platformEndpoint);
      // login returns the user; fall back to a fresh /me if not present
      const nextUser = res?.user || (await refresh());
      if (res?.user) setUser(res.user);
      return nextUser;
    },
    [refresh]
  );

  const logout = useCallback(async () => {
    await partnerLogout();
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      partner: user?.partner || null,
      isAuthenticated: Boolean(user),
      // Role drives what the portal SHOWS. It never decides what the platform
      // ALLOWS: every admin-only endpoint re-checks the role server-side, so a
      // member who forces the route gets 403 rather than a working page.
      // Anything that is not an explicit 'admin' is a member.
      role: user?.role === 'admin' ? 'admin' : 'member',
      isAdmin: user?.role === 'admin',
      isInitializing,
      login,
      logout,
      refresh
    }),
    [user, isInitializing, login, logout, refresh]
  );

  return <PartnerAuthContext.Provider value={value}>{children}</PartnerAuthContext.Provider>;
}

PartnerAuthProvider.propTypes = { children: PropTypes.node };
